import { CircularProgress, Tab, Tabs, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Container, SideDrawerContainer } from '../../components'
import { axiosInstance } from '../../helpers'
import {
    InspectionWithReportDate,
    Lease,
    Report,
    ReportTypes,
    Schedule,
} from '../../models'
import { InspectionReportTable } from './InspectionReportTable'
import { ReportsDrawer } from './ReportsDrawer'
import { WorkorderReportTable } from './WorkorderReportTable'
import { ReportTable } from './ReportTable'
import { PaginatedReportTable } from './PaginatedReportTable'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { typographyStyle } from '../../theme/typographyStyle'

export const ReportsHome = () => {
    const theme = useTheme()
    const history = useHistory()
    const location = useLocation()

    const [scheduleList, setScheduleList] = useState<Schedule[]>([])
    const [inspectionList, setInspectionList] = useState<
        InspectionWithReportDate[]
    >([])

    const [leaseSyncReports, setLeaseSyncReports] = useState<Report[]>([])
    const [bedStatusSyncReports, setBedStatusSyncReports] = useState<Report[]>(
        [],
    )
    const [damageReports, setDamageReports] = useState<Report[]>([])

    const [unitNotesReports, setUnitNotesReports] = useState<Report[]>([])
    const [leaseAuditReports, setLeaseAuditReports] = useState<Report[]>([])
    const [tenantDamageReports, setTenantDamageReports] = useState<Report[]>([])
    const [
        tenantDamageTotalCount,
        setTenantDamageTotalCount,
    ] = useState<number>(0)
    const [tenantDamagePage, setTenantDamagePage] = useState<number>(0)
    const [tenantDamagePageSize, setTenantDamagePageSize] = useState<number>(10)
    const [tenantDamageSortBy, setTenantDamageSortBy] = useState<string>(
        'creation_time',
    )
    const [tenantDamageSortDir, setTenantDamageSortDir] = useState<
        'asc' | 'desc'
    >('desc')
    const [
        tenantDamageSearchQuery,
        setTenantDamageSearchQuery,
    ] = useState<string>('')

    // Reset pagination when component mounts
    useEffect(() => {
        setTenantDamagePage(0)
        setTenantDamageSortBy('creation_time')
        setTenantDamageSortDir('desc')
        setTenantDamageSearchQuery('')
    }, [])

    const [selectedSchedule, setSelectedSchedule] = useState<Schedule>()
    const [
        selectedInspection,
        setSelectedInspection,
    ] = useState<InspectionWithReportDate>()

    const WORKORDER_TAB = 0
    const INSPECTION_TAB = 1
    const LEASE_SYNCING_TAB = 2
    const BED_STATUS_SYNCING_TAB = 3
    const DAMAGE_TAB = 4
    const UNIT_NOTES_TAB = 5
    const LEASE_AUDIT_TAB = 6
    const TENANT_DAMAGE_TAB = 7
    const TabHashMap: { [key: number]: string } = {
        [WORKORDER_TAB]: 'workorders',
        [INSPECTION_TAB]: 'inspections',
        [LEASE_SYNCING_TAB]: 'lease-syncing',
        [BED_STATUS_SYNCING_TAB]: 'bed-status-syncing',
        [DAMAGE_TAB]: 'damages',
        [UNIT_NOTES_TAB]: 'unit-notes',
        [LEASE_AUDIT_TAB]: 'lease-audits',
        [TENANT_DAMAGE_TAB]: 'tenant-damages',
    }

    const hashToTab = Object.fromEntries(
        Object.entries(TabHashMap).map(([key, value]) => [
            value,
            parseInt(key),
        ]),
    )

    useEffect(() => {
        const hash = location.hash.replace('#', '')
        if (hash in hashToTab) {
            setSelectedTab(hashToTab[hash])
        }
    }, [location])

    const [selectedTab, setSelectedTab] = useState<number>(() => {
        const hash = location.hash.replace('#', '')
        return hash in hashToTab ? hashToTab[hash] : WORKORDER_TAB
    })

    const [drawerOpen, setDrawerOpen] = useState(false)

    // Individual loading states for each tab
    const [workorderLoading, setWorkorderLoading] = useState(false)
    const [inspectionLoading, setInspectionLoading] = useState(false)
    const [leaseSyncLoading, setLeaseSyncLoading] = useState(false)
    const [bedStatusSyncLoading, setBedStatusSyncLoading] = useState(false)
    const [damageLoading, setDamageLoading] = useState(false)
    const [unitNotesLoading, setUnitNotesLoading] = useState(false)
    const [leaseAuditLoading, setLeaseAuditLoading] = useState(false)
    const [tenantDamageLoading, setTenantDamageLoading] = useState(false)

    // Individual fetch functions for each report type
    const fetchWorkorders = async () => {
        if (scheduleList.length === 0 && !workorderLoading) {
            setWorkorderLoading(true)
            try {
                const response = await axiosInstance.get(
                    `workorder/schedule/`,
                    {
                        params: { has_report: true },
                    },
                )
                setScheduleList(response.data)
            } catch (error: any) {
                toast.error('Error fetching workorders:', error)
            } finally {
                setWorkorderLoading(false)
            }
        }
    }

    const fetchInspections = async () => {
        if (inspectionList.length === 0 && !inspectionLoading) {
            setInspectionLoading(true)
            try {
                const response = await axiosInstance.get('inspection/', {
                    params: { has_report: true },
                })
                setInspectionList(response.data)
            } catch (error: any) {
                toast.error('Error fetching inspections:', error)
            } finally {
                setInspectionLoading(false)
            }
        }
    }

    const fetchLeaseSyncReports = async () => {
        if (leaseSyncReports.length === 0 && !leaseSyncLoading) {
            setLeaseSyncLoading(true)
            try {
                const response = await axiosInstance.get(`stats/reports/`, {
                    params: { report_types: ReportTypes.LEASE_SYNC_REPORT },
                })
                setLeaseSyncReports(response.data)
            } catch (error: any) {
                toast.error('Error fetching lease sync reports:', error)
            } finally {
                setLeaseSyncLoading(false)
            }
        }
    }

    const fetchBedStatusSyncReports = async () => {
        if (bedStatusSyncReports.length === 0 && !bedStatusSyncLoading) {
            setBedStatusSyncLoading(true)
            try {
                const response = await axiosInstance.get(`stats/reports/`, {
                    params: {
                        report_types: ReportTypes.BED_STATUS_SYNC_REPORT,
                    },
                })
                setBedStatusSyncReports(response.data)
            } catch (error: any) {
                toast.error('Error fetching bed status sync reports:', error)
            } finally {
                setBedStatusSyncLoading(false)
            }
        }
    }

    const fetchDamageReports = async () => {
        if (damageReports.length === 0 && !damageLoading) {
            setDamageLoading(true)
            try {
                const response = await axiosInstance.get(`stats/reports/`, {
                    params: {
                        report_types: ReportTypes.DAMAGE_AND_TRANSACTION_REPORT,
                    },
                })
                setDamageReports(response.data)
            } catch (error: any) {
                toast.error('Error fetching damage reports:', error)
            } finally {
                setDamageLoading(false)
            }
        }
    }

    const fetchUnitNotesReports = async () => {
        if (unitNotesReports.length === 0 && !unitNotesLoading) {
            setUnitNotesLoading(true)
            try {
                const response = await axiosInstance.get(`stats/reports/`, {
                    params: { report_types: ReportTypes.UNIT_NOTES_REPORT },
                })
                setUnitNotesReports(response.data)
            } catch (error: any) {
                toast.error('Error fetching unit notes reports:', error)
            } finally {
                setUnitNotesLoading(false)
            }
        }
    }

    const fetchLeaseAuditReports = async () => {
        if (leaseAuditReports.length === 0 && !leaseAuditLoading) {
            setLeaseAuditLoading(true)
            try {
                const response = await axiosInstance.get(`stats/reports/`, {
                    params: { report_types: ReportTypes.LEASE_AUDIT_REPORT },
                })
                setLeaseAuditReports(response.data)
            } catch (error: any) {
                toast.error('Error fetching lease audit reports:', error)
            } finally {
                setLeaseAuditLoading(false)
            }
        }
    }

    const fetchTenantDamageReports = async (
        page: number = 0,
        pageSize: number = 10,
        sortBy?: string,
        sortDir?: 'asc' | 'desc',
        searchQuery?: string,
    ) => {
        setTenantDamageLoading(true)
        try {
            const offset = page * pageSize
            const orderingParam = sortDir === 'desc' ? `-${sortBy}` : sortBy

            const params: any = {
                report_types: ReportTypes.TENANT_DAMAGE_REPORT,
                limit: pageSize,
                offset: offset,
                ordering: orderingParam,
            }

            // Add search parameter if provided
            if (searchQuery && searchQuery.trim()) {
                params.search = searchQuery.trim()
            }

            const response = await axiosInstance.get(`stats/reports/`, {
                params,
            })

            // Handle both paginated and non-paginated responses
            if (response.data.results && response.data.count !== undefined) {
                // Paginated response
                setTenantDamageReports(response.data.results)
                setTenantDamageTotalCount(response.data.count)
            } else {
                // Non-paginated response (fallback)
                setTenantDamageReports(response.data)
                setTenantDamageTotalCount(response.data.length)
            }
        } catch (error: any) {
            toast.error('Error fetching tenant damage reports:', error)
        } finally {
            setTenantDamageLoading(false)
        }
    }

    // Fetch data when tab changes
    useEffect(() => {
        switch (selectedTab) {
            case WORKORDER_TAB:
                fetchWorkorders()
                break
            case INSPECTION_TAB:
                fetchInspections()
                break
            case LEASE_SYNCING_TAB:
                fetchLeaseSyncReports()
                break
            case BED_STATUS_SYNCING_TAB:
                fetchBedStatusSyncReports()
                break
            case DAMAGE_TAB:
                fetchDamageReports()
                break
            case UNIT_NOTES_TAB:
                fetchUnitNotesReports()
                break
            case LEASE_AUDIT_TAB:
                fetchLeaseAuditReports()
                break
            case TENANT_DAMAGE_TAB:
                // Reset pagination and sorting when switching to this tab
                setTenantDamagePage(0)
                setTenantDamageSortBy('creation_time')
                setTenantDamageSortDir('desc')
                setTenantDamageSearchQuery('')
                fetchTenantDamageReports(
                    0,
                    tenantDamagePageSize,
                    'creation_time',
                    'desc',
                    '',
                )
                break
        }
    }, [selectedTab])

    const closeDrawer = () => {
        setDrawerOpen(false)
        setSelectedInspection(undefined)
        setSelectedSchedule(undefined)
    }

    const handleTabChange = (_: any, newValue: number) => {
        if (newValue !== selectedTab) {
            setSelectedTab(newValue)
            history.push(`/reports#${TabHashMap[newValue]}`)
            closeDrawer()
        }
    }

    // Pagination handlers for tenant damage reports
    const handleTenantDamagePageChange = (newPage: number) => {
        setTenantDamagePage(newPage)
        fetchTenantDamageReports(
            newPage,
            tenantDamagePageSize,
            tenantDamageSortBy,
            tenantDamageSortDir,
            tenantDamageSearchQuery,
        )
    }

    const handleTenantDamagePageSizeChange = (newPageSize: number) => {
        setTenantDamagePageSize(newPageSize)
        setTenantDamagePage(0) // Reset to first page
        fetchTenantDamageReports(
            0,
            newPageSize,
            tenantDamageSortBy,
            tenantDamageSortDir,
            tenantDamageSearchQuery,
        )
    }

    // Sort handler for tenant damage reports
    const handleTenantDamageSort = (
        sortBy: string,
        sortDir: 'asc' | 'desc',
    ) => {
        setTenantDamageSortBy(sortBy)
        setTenantDamageSortDir(sortDir)
        setTenantDamagePage(0) // Reset to first page when sorting changes
        fetchTenantDamageReports(
            0,
            tenantDamagePageSize,
            sortBy,
            sortDir,
            tenantDamageSearchQuery,
        )
    }

    // Search handler for tenant damage reports
    const handleTenantDamageSearch = (searchQuery: string) => {
        setTenantDamageSearchQuery(searchQuery)
        setTenantDamagePage(0) // Reset to first page when searching
        fetchTenantDamageReports(
            0,
            tenantDamagePageSize,
            tenantDamageSortBy,
            tenantDamageSortDir,
            searchQuery,
        )
    }

    // Helper function to check if current tab is loading
    const isCurrentTabLoading = () => {
        switch (selectedTab) {
            case WORKORDER_TAB:
                return workorderLoading
            case INSPECTION_TAB:
                return inspectionLoading
            case LEASE_SYNCING_TAB:
                return leaseSyncLoading
            case BED_STATUS_SYNCING_TAB:
                return bedStatusSyncLoading
            case DAMAGE_TAB:
                return damageLoading
            case UNIT_NOTES_TAB:
                return unitNotesLoading
            case LEASE_AUDIT_TAB:
                return leaseAuditLoading
            case TENANT_DAMAGE_TAB:
                return tenantDamageLoading
            default:
                return false
        }
    }

    return (
        <SideDrawerContainer open={drawerOpen}>
            <Container
                flex={1}
                direction="column"
                style={{
                    height: 'calc(100vh - 104px)',
                    overflow: 'hidden', // Ensure the container doesn't overflow
                }}
            >
                {/* Header */}
                <Container>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        Reports
                    </span>
                </Container>

                {/* Table */}
                <Container
                    style={{
                        overflowX: 'auto',
                        width: '100%',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        flex: 1,
                    }}
                >
                    <Tabs
                        style={{
                            width: '100%',
                            backgroundColor: theme.palette.grey[300],
                        }}
                        value={selectedTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Work Orders" />
                        <Tab label="Inspections" />
                        <Tab label="Lease Syncing" />
                        <Tab label="Bed Status Syncing" />
                        <Tab label="Damages" />
                        <Tab label="Unit Notes" />
                        <Tab label="Lease Audits" />
                        <Tab label="Tenant Damages" />
                    </Tabs>
                    <Container
                        style={{
                            flex: 1,
                            backgroundColor: theme.palette.grey[100],
                            overflowY: 'auto',
                        }}
                    >
                        {selectedTab === WORKORDER_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <WorkorderReportTable
                                    scheduleList={scheduleList}
                                    onClick={(newSchedule) => {
                                        setSelectedSchedule(newSchedule)
                                        setDrawerOpen(true)
                                    }}
                                />
                            ))}
                        {selectedTab === INSPECTION_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <InspectionReportTable
                                    inspectionList={inspectionList}
                                    onClick={(inspection) => {
                                        setSelectedInspection(inspection)
                                        setDrawerOpen(true)
                                    }}
                                />
                            ))}
                        {selectedTab === LEASE_SYNCING_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <ReportTable reports={leaseSyncReports} />
                            ))}
                        {selectedTab === BED_STATUS_SYNCING_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <ReportTable reports={bedStatusSyncReports} />
                            ))}
                        {selectedTab === DAMAGE_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <ReportTable reports={damageReports} />
                            ))}
                        {selectedTab === UNIT_NOTES_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <ReportTable reports={unitNotesReports} />
                            ))}
                        {selectedTab === LEASE_AUDIT_TAB &&
                            (isCurrentTabLoading() ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '80vh',
                                    }}
                                >
                                    <CircularProgress size={80} />
                                </Container>
                            ) : (
                                <ReportTable reports={leaseAuditReports} />
                            ))}
                        {selectedTab === TENANT_DAMAGE_TAB && (
                            <PaginatedReportTable
                                reports={tenantDamageReports}
                                totalCount={tenantDamageTotalCount}
                                page={tenantDamagePage}
                                pageSize={tenantDamagePageSize}
                                onPageChange={handleTenantDamagePageChange}
                                onPageSizeChange={
                                    handleTenantDamagePageSizeChange
                                }
                                onSort={handleTenantDamageSort}
                                onSearch={handleTenantDamageSearch}
                                loading={tenantDamageLoading}
                            />
                        )}
                    </Container>
                </Container>
            </Container>

            <ReportsDrawer
                open={drawerOpen}
                handleClose={closeDrawer}
                inspectionId={selectedInspection?.id}
                scheduleId={selectedSchedule?.id}
            />
        </SideDrawerContainer>
    )
}
